import React, { useState, useEffect } from 'react';
import API from "../http/API";
import Box from '@mui/material/Box';
import Grid2 from "@mui/material/Grid2";
import ListItem from '@mui/material/ListItem';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import SyncIcon from '@mui/icons-material/Sync';
import FilterListIcon from '@mui/icons-material/FilterList'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField';
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Backdrop from '@mui/material/Backdrop';
import {
    useCurrentUser,
    useAuthToken,
    useRequestUrl,
    useCitepayUser,
    useAgencies,
    useDefaultAgency,
    useNearPasswordExpiration,
    useAgencyCourt,
    useCourtsByAgency,
    useTerminals
} from "../context/contextHooks";
import { useForm } from "react-hook-form";
import { filterUsers } from "./filterUsers";
import UserEdit from "./UserEdit";
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import AlertMessage from "../utils/AlertMessage";
import UpdateUserPassword from "./UpdateUserPassword";
import {useSxStyles as sx} from "../styles/sxStyles";

const ManageUsers = () => {
    const [currentUser,] = useCurrentUser();
    const [authToken,] = useAuthToken();
    const [requestUrl,] = useRequestUrl();
    const [usersData, setUsersData] = useState(null);
    const [filteredUsers, setFilteredUsers] = useState(null);
    const citepayUser = useCitepayUser();
    const { register, handleSubmit } = useForm();
    const [errorMessage, setErrorMessage] = useState('');
    const [responseMessage, setResponseMessage] = useState('');
    const [courtsByAgency,] = useCourtsByAgency();
    const [agencyCourt, setAgencyCourt] = useAgencyCourt();
    const [,setAgencies] = useAgencies();
    const [agencyData, setAgencyData] = useState(null);
    const [,setDefaultAgency] = useDefaultAgency();
    const [agency, setAgency] = useState(null);
    const [loading, setLoading] = useState(false)
    const [actionResponse, setActionResponse] = useState(null);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(8);
    const isNearPasswordExpiration = useNearPasswordExpiration();
    const [filterRequest, setFilterRequest] = useState(null);
    const [terminals, setTerminals] = useTerminals();

    const clearMessages = () => {
        setErrorMessage(null);
        setResponseMessage('');
        setActionResponse(null);
    }

    const retrieveUsers = (agencyId) => {
        if (!loading) {
            clearMessages();
            setLoading(true);
            const url = `${requestUrl}/users`;
            const request = `{"agencyId": "${agencyId}"}`;
            API.postRetrieve(url, request, 'users', `Bearer ${authToken}`, setActionResponse);
        }
    }

    const retrieveTerminals = () => {
        clearMessages();
        setLoading(true);
        const url = `${requestUrl}/pos/terminalsByAgency/${agency}`;
        API.getRetrieve(url, 'getTerminals', `Bearer ${authToken}`, setActionResponse);
    }

    const applyFilter = (formRequest) => {
        setActionResponse(null);
        setFilterRequest(formRequest);
        setFilteredUsers(filterUsers(formRequest, boxState, usersData));
        setPage(0);
    }

    const selectAgency = (e) => {
        const value = e.currentTarget.value;
        setAgency(value);
        setDefaultAgency(value);
        retrieveUsers(value);
    }

    const [boxState, setBoxState] = useState({
        admin: false,
        userRole: false,
        citepayAdmin: false,
    });

    const { admin, userRole, citepayAdmin } = boxState;

    const handleBox = (e) => {
        setBoxState({ ...boxState, [e.target.name]: e.target.checked });
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    }

    useEffect(() => {
        if (loading && actionResponse) {
            if (actionResponse.error) {
                setErrorMessage(actionResponse.errorMessage ? actionResponse.errorMessage : actionResponse.message)
            } else if (actionResponse.requestType === 'users') {
                setErrorMessage(null);
                setUsersData(actionResponse.data);
                if (filterRequest !== null || boxState.admin || boxState.citepayAdmin || boxState.userRole) {
                    applyFilter(filterRequest);
                } else {
                    setFilteredUsers(actionResponse.data);
                }
            } else if (actionResponse.message === 'agencies') {
                setErrorMessage(null);
                setAgencies(actionResponse.data);
                setAgencyData(actionResponse.data);
                setLoading(false);
                setActionResponse(null);
            } else if (actionResponse.requestType==='getTerminals') {
                setTerminals(actionResponse.terminals);
            }
            setLoading(false);
        }
    }, [actionResponse, loading]);

    useEffect(() => {
        if (!loading && citepayUser && !agencyData) {
            const url = `${requestUrl}/agencies`
            clearMessages();
            setLoading(true);
            API.getRetrieve(url, 'agencies', `Bearer ${authToken}`, setActionResponse);
        }
    }, [citepayUser]);

    useEffect(() => {
        if (!loading && currentUser && currentUser.agencyId && !agency) {
            setAgency(currentUser.agencyId);
            setDefaultAgency(currentUser.agencyId);
            retrieveUsers(currentUser.agencyId);
        }
    }, [currentUser]);

    useEffect(() => {
        if (!loading && agencyCourt && currentUser.posPayments && !terminals) {
            retrieveTerminals();
        }
    }, [agencyCourt, currentUser, terminals, loading])

    useEffect(() => {
        if (!agencyCourt && courtsByAgency && courtsByAgency.courts && courtsByAgency.courts.length === 1) {
            setAgencyCourt(courtsByAgency.courts[0]);
        }
    }, [courtsByAgency, agencyCourt]);

    return <>
        <Typography variant="h4" color='primary' sx={sx.header}>Manage Users</Typography>
        {isNearPasswordExpiration ? <AlertMessage severity="warning" title="Password Expiration Warning" msg="Your password will expire soon. Reset the password at your earliest convenience." /> : null}
        <Grid2
            container
            spacing={2}
            alignItems="top"
            justifyContent="center"
        >
            <Grid2 size={4}>
                { citepayUser ?
                    <Paper elevation={4} sx={sx.controlPanel}>
                        <FormControl
                            fullWidth
                        >
                            <Typography sx={sx.inputLabel} variant="h5" color='primary'>Select an Agency</Typography>
                            { agencyData !== null ?
                                <Select
                                    id='agency'
                                    name='agency'
                                    variant='outlined'
                                    fullWidth
                                    sx={{height: '2rem'}}
                                    native
                                    onChange={selectAgency}
                                >
                                    <option key='-1' value='-1'>None selected</option>
                                    {agencyData.map(agency => {
                                        return (
                                            <option key={agency.key} value={agency.value}>{agency.text}</option>
                                        );
                                    })}
                                </Select>
                                :
                                <Backdrop sx={sx.backdrop} open={loading}>
                                    <CircularProgress color="inherit" />
                                </Backdrop>
                            }
                        </FormControl>
                    </Paper>
                    :
                    null
                }
                <Paper elevation={3} sx={sx.controlPanel}>
                    <Typography sx={sx.inputLabel} variant="h5" color='primary'>Filter Users</Typography>
                    <Grid2 container spacing={1} alignItems='top'>
                        <Grid2 size={6}>
                            <FormControl fullWidth
                                {...register('lname')}
                                >
                            <FormLabel shrink={true} sx={sx.inputLabel}>Last Name</FormLabel>
                            <TextField
                                id="lname"
                                name="lname"
                                variant='outlined'
                                fullWidth
                                slotProps={{
                                    htmlInput: {sx: sx.textbox}
                                }}
                            />
                            </FormControl>
                        </Grid2>
                        <Grid2 size={6} >
                            <FormControl
                                fullWidth
                                {...register('uname')}
                                >
                            <FormLabel shrink={true} sx={sx.inputLabel}>Username</FormLabel>
                            <TextField
                                id="uname"
                                name="uname"
                                variant='outlined'
                                fullWidth
                                slotProps={{
                                    htmlInput: {sx: sx.textbox}
                                }}

                            />
                            </FormControl>
                        </Grid2>
                        <Grid2 size={12}>
                            <FormControl component="fieldset" sx={sx.formControl}>
                                <FormLabel component="legend">
                                    <Typography variant="subtitle1" color='primary'>
                                        Security Roles
                                    </Typography>
                                </FormLabel>
                                <FormGroup row>
                                    <FormControlLabel
                                        control={<Checkbox color='primary' checked={admin} onChange={handleBox} name="admin" />}
                                        label="Admin"
                                        sx={sx.inputLabel}
                                    />
                                    <FormControlLabel
                                        control={<Checkbox color='primary' checked={userRole} onChange={handleBox} name="userRole" />}
                                        label="User"
                                        sx={sx.inputLabel}
                                    />
                                    {citepayUser ?
                                        <FormControlLabel
                                            control={<Checkbox color='primary' checked={citepayAdmin} onChange={handleBox} name="citepayAdmin" />}
                                            label="CitePay Admin"
                                            sx={sx.inputLabel}
                                        />
                                        : null
                                    }
                                </FormGroup>
                            </FormControl>
                        </Grid2>
                    </Grid2>
                    <div>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<FilterListIcon/>}
                            fullWidth
                            sx={{...sx.button, marginLeft: '-2px'}}
                            onClick={handleSubmit(applyFilter)}
                        >
                            Apply Filter
                        </Button>
                    </div>
                </Paper>
                <Box component='span' display='flex' flexDirection="row-reverse">
                    <UserEdit
                        description='Add user'
                        callback={setActionResponse}
                        setLoading={setLoading}
                        terminals={terminals}
                        locations={agencyCourt && agencyCourt.paymentSourceSystems ? agencyCourt.paymentSourceSystems : null}
                    />
                    <Button variant="contained"
                            color="primary"
                            startIcon={<SyncIcon/>}
                            sx={sx.button}
                            onClick={() => {
                                retrieveUsers(agency)
                            }}
                    >
                        Refresh Users
                    </Button>
                </Box>
            </Grid2>
            <Grid2 size={8}>
                <Typography variant="h5" color='primary' sx={{...sx.controlPanel, marginTop:'.5rem'}}>Users</Typography>
                {errorMessage ?
                    <Alert severity="error" sx={{marginBottom: '.5rem'}}>
                        <AlertTitle>Error</AlertTitle>
                        {errorMessage}
                    </Alert>
                    : null
                }
                {responseMessage ?
                    <Alert severity="info">
                        {responseMessage}
                    </Alert>
                    : null
                }
                {filteredUsers !== null ?
                    <TableContainer component={Paper} elevation={3} sx={sx.users}>
                        <Table sx={sx.list} size='small'>
                            <TableBody>
                                {filteredUsers && filteredUsers.length > 0 ? filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => {
                                    return (
                                        (<TableRow>
                                            <TableCell>
                                                <ListItem alignItems="flex-start" disableGutters
                                                          sx={user.enabled ? sx.listItem : sx.listItemDisabled}>
                                                    <ListItemText
                                                        sx={sx.listText}
                                                        primary={`${user.lastName}, ${user.firstName} (${user.username}) ${citepayUser ? '- [' + user.externalId + ']' : ''}`}
                                                        secondary={
                                                            <Grid2
                                                                container
                                                                spacing={2}
                                                                alignItems="top"
                                                                style={{width: '100%'}}
                                                            >
                                                                <Grid2 size={4}>
                                                                    <Typography
                                                                        component="span"
                                                                        variant='body2'
                                                                        sx={{...sx.inline, display: 'inline'}}
                                                                        color="textPrimary"
                                                                    >
                                                                        {user.enabled ?
                                                                            user.isAdmin ? 'Admin' : (user.isJsi ? 'JSI User' : 'User') :
                                                                            '(DISABLED)'
                                                                        }
                                                                    </Typography>
                                                                </Grid2>
                                                                <Grid2 size={6}>
                                                                    <Typography
                                                                        component="span"
                                                                        variant='body2'
                                                                        sx={{...sx.inline, display: 'inline'}}
                                                                        color="textPrimary"
                                                                    >
                                                                        Last Login: &nbsp;{user.lastLogin}
                                                                    </Typography>
                                                                </Grid2>
                                                                <Grid2 size="grow">&nbsp;</Grid2>
                                                            </Grid2>
                                                        }
                                                        slotProps={{
                                                            primary: {variant: 'subtitle1', }
                                                        }}
                                                    />
                                                    <ListItemIcon alt="Edit this user" title={`Edit ${user.firstName} ${user.lastName}`}>
                                                        <UserEdit
                                                            editUser={user}
                                                            description='Edit user'
                                                            callback={setActionResponse}
                                                            setLoading={setLoading}
                                                            terminals={terminals}
                                                            locations={agencyCourt && agencyCourt.paymentSourceSystems ? agencyCourt.paymentSourceSystems : null}
                                                        />
                                                    </ListItemIcon>
                                                </ListItem>
                                            </TableCell>
                                        </TableRow>)
                                    );
                                }):null
                                }
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TablePagination
                                        id="tablePagination"
                                        rowsPerPageOptions={[8, 15, 25, 50, { label: 'All', value: -1}]}
                                        component='div'
                                        count={filteredUsers.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        slotProps={{
                                            select: {
                                                inputProps: { 'aria-label': 'rows per page' },
                                                native: true
                                            }
                                        }}
                                    />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                    : null
                }
            </Grid2>
            <UpdateUserPassword />
        </Grid2>
    </>;
}

export default ManageUsers;
